import { useEffect, useState } from "react";
import { ISurvey, ISurveyAnswer, ISurveyOptionAnswer, ISurveyQuestion, ISurveyQuestionAnswer, ISurveyQuestionOption, fetchSurvey, postSurvey } from "../../infra/api/SurveyApi";
import { ISurveyProps } from "../../ui/etc/Survey/Survey";
import { useBaseParam } from "../hooks/useBaseParam";
import { useRecoilState } from "recoil";
import * as Atom from '../recoil/index';
import useToast from "../hooks/useToast";

const useSurvey = ({ surveyType, surveySeq, targetSeq, isBottomSheet, onSave, onClickBottomSheetClose }: ISurveyProps) => {
    const [survey, setSurvey] = useState<ISurvey>({
      surveySeq: 0,
      title: "",
      subtitle: "",
      questions: [],
    });

    const [answerMap] = useState<Map<number, ISurveyQuestionAnswer>>(new Map());
    const [enableSave, setEnableSave] = useState(false);
    const [requiredQuestions, setRequestQuestions] = useState<ISurveyQuestion[]>([]);
    const [bottomSheet] = useState(isBottomSheet ?? false);
    const [personal] = useRecoilState(Atom.personalInfo);
    const [surveyAnswer, setSurveyAnswer] = useState<ISurveyAnswer | undefined>(undefined);
    const { addToast } = useToast();
    

    const getSurveyQuestions = useBaseParam(fetchSurvey, { type: surveyType, surveySeq: surveySeq?? null })
    useEffect(() => {
      // Get Survey Data
      getSurveyQuestions().then(responseSurvey => {
        if (responseSurvey.data && responseSurvey.data.isSucceed && responseSurvey.data.result) {
          const survey = responseSurvey.data.result as ISurvey;
          setSurvey(survey);
          setRequestQuestions(survey.questions.filter(q => q.requiredYn === "Y"));
        }
      });
    }, []);

    const onChangeSelectedOptions = (question: ISurveyQuestion, selectedOptions: ISurveyOptionAnswer[]) => {
      const answer = getAnswer(question);
      answer.selectedOptions = selectedOptions;

      canSave();
    };

    const canSave = () => {
      const hasNotAnswerOfRequired = requiredQuestions.find(r => !hasAnswer(r));
      const enable = hasNotAnswerOfRequired ? false : true;
      setEnableSave(enable);
    }

    const getAnswer = (question: ISurveyQuestion): ISurveyQuestionAnswer => {
      let answer = answerMap.get(question.questionSeq);
      if (answer)
        return answer;
      answer = {
        questionSeq: question.questionSeq,
        textAnswer: "",
        selectedOptions: []
      };
      answerMap.set(question.questionSeq, answer);
      return answer;
    }

    const onChangeText = (question: ISurveyQuestion, option: ISurveyQuestionOption | null, text: string) => {
      const answer = getAnswer(question);
      if (option) {
        const selectedOption = answer.selectedOptions.find(o => o.optionSeq === option.optionSeq);
        if (selectedOption) {
          selectedOption.textAnswer = text;
        }
      } else {
        answer.textAnswer = text;
      }

      canSave();
    }

    const hasAnswer = (question: ISurveyQuestion): boolean => {
      const answer = answerMap.get(question.questionSeq) as ISurveyQuestionAnswer;
      if (!answer)
        return false;
      if (question.questionType === "01") {
        const selected = (answer.selectedOptions && answer.selectedOptions.length > 0);
        // 직접입력은 5글자 이상이여야 함.
        const hasNotInputOption = answer.selectedOptions.filter(o => o.isDirectInput).find(d => !d.textAnswer || d.textAnswer.trim().length < 5);
        return (selected && (!hasNotInputOption));
      } else {
        return (answer.textAnswer.length > 0);
      }
    }

    const saveSurvey = useBaseParam(postSurvey, surveyAnswer);
    useEffect(() => {
      if (surveyAnswer && surveyAnswer.surveySeq && surveyAnswer.custSeq) {
        saveSurvey().then((response) => {
          if (response.data.isSucceed) {
            addToast("소중한 의견 감사해요.\n덕분에 좀 더 나은 서비스가 될 수 있을 거 같아요!");
            if (isBottomSheet && onClickBottomSheetClose) {
              onClickBottomSheetClose();
            }
            if (onSave) {
              onSave();
            }
          }
        });
      }
    }, [surveyAnswer]);

    const save = async () => {
      const request = {
        surveySeq: survey.surveySeq ,
        custSeq: personal?.custSeq,
        targetSeq: targetSeq,
        answers: Array.from(answerMap.values())
      };

      setSurveyAnswer(request);
    }

    const onClickCloseButton = () => {
      if (onClickBottomSheetClose) {
        onClickBottomSheetClose();
      }
    }

    return {
      survey,
      enableSave,
      bottomSheet,
      onChangeSelectedOptions,
      onChangeText,
      save,
      onClickCloseButton,
    };
}

export default useSurvey;